import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useRef, useState } from 'react';

import { showToast } from '../../helpers';
import { apiMessage } from '../../requests/opsInstructors';
import {
  TOpsDocument,
  addOpsDocumentLink,
  deleteOpsDocument,
  downloadOpsDocument,
  getOpsDocuments,
  uploadOpsDocument,
} from '../../requests/opsDocuments';
import { btnGhost, btnSecondary, Card, inputClass } from './ui';

const ACCEPT = '.pdf,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,.csv,.txt,.zip';

const fmtSize = (n: number | null) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/**
 * Attachments on a record: list, add a link, upload a file (25 MB; pdf,
 * docx, xlsx, pptx, png, jpg, csv, txt, zip), download, remove. Mount it
 * anywhere with the record's id and object — the account 360 does today.
 */
const AttachmentsPanel: React.FC<{
  recordId: string;
  recordObject: string;
  title?: string;
}> = ({ recordId, recordObject, title = 'Attachments' }) => {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['opsDocuments', recordId],
    queryFn: () => getOpsDocuments(recordId),
    enabled: Boolean(recordId),
  });
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['opsDocuments', recordId] });

  const addLink = useMutation({
    mutationFn: () =>
      addOpsDocumentLink({
        recordId,
        recordObject,
        name: linkName.trim() || linkUrl.trim(),
        url: linkUrl.trim(),
      }),
    onSuccess: () => {
      showToast('Link added', 'success');
      setLinkOpen(false);
      setLinkName('');
      setLinkUrl('');
      refresh();
    },
    onError: (e: unknown) => showToast(apiMessage(e), 'error'),
  });

  const upload = useMutation({
    mutationFn: (file: File) =>
      uploadOpsDocument(file, recordId, recordObject, setProgress),
    onSuccess: (d) => {
      showToast(`Uploaded ${d.name}`, 'success');
      refresh();
    },
    onError: (e: unknown) => showToast(apiMessage(e), 'error'),
    onSettled: () => {
      setProgress(null);
      if (fileInput.current) fileInput.current.value = '';
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteOpsDocument(id),
    onSuccess: () => {
      showToast('Removed', 'success');
      refresh();
    },
    onError: (e: unknown) => showToast(apiMessage(e), 'error'),
  });

  const onDownload = async (d: TOpsDocument) => {
    if (d.kind === 'link' && d.url) {
      window.open(d.url, '_blank', 'noopener');
      return;
    }
    setBusyId(d.id);
    try {
      await downloadOpsDocument(d);
    } catch (e) {
      showToast(apiMessage(e), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const items = data?.items ?? [];

  return (
    <Card
      title={data ? `${title} · ${data.total}` : title}
      right={
        <span className="flex items-center gap-2">
          <button
            type="button"
            className={btnGhost}
            onClick={() => setLinkOpen((v) => !v)}
          >
            Add link
          </button>
          <button
            type="button"
            className={btnSecondary}
            disabled={progress != null}
            onClick={() => fileInput.current?.click()}
          >
            {progress != null ? `Uploading ${progress}%` : 'Upload file'}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
            }}
          />
        </span>
      }
    >
      <div className="px-5 py-4">
        {linkOpen && (
          <form
            className="mb-4 grid gap-2 rounded-lg border border-white-three p-3 sm:grid-cols-[1fr_2fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              if (linkUrl.trim()) addLink.mutate();
            }}
          >
            <input
              className={inputClass}
              placeholder="Name (optional)"
              value={linkName}
              onChange={(e) => setLinkName(e.target.value)}
            />
            <input
              className={inputClass}
              placeholder="https://…"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              required
            />
            <button
              type="submit"
              className={btnSecondary}
              disabled={addLink.isPending || !linkUrl.trim()}
            >
              Save link
            </button>
          </form>
        )}
        {progress != null && (
          <div className="mb-3 h-1.5 w-full overflow-hidden rounded bg-white-three">
            <div
              className="h-full bg-blue-primary transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        {isLoading ? (
          <div className="text-sm text-gray-two">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-gray-two">
            Nothing attached yet. Files up to 25 MB (pdf, docx, xlsx, pptx, png,
            jpg, csv, txt, zip) or a link.
          </div>
        ) : (
          <ul className="grid gap-1">
            {items.map((d) => (
              <li
                key={d.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white-three px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <button
                    type="button"
                    className="max-w-full truncate text-left font-medium text-gray-four hover:text-blue-primary"
                    title={d.kind === 'link' ? d.url ?? '' : d.name}
                    onClick={() => onDownload(d)}
                    disabled={busyId === d.id}
                  >
                    {d.kind === 'link' ? '🔗 ' : '📎 '}
                    {d.name}
                  </button>
                  <div className="text-xs text-gray-bold">
                    {[
                      d.kind === 'file' ? fmtSize(d.size) : 'link',
                      d.uploadedBy,
                      fmtWhen(d.at),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
                <span className="flex items-center gap-1">
                  <button
                    type="button"
                    className={btnGhost}
                    onClick={() => onDownload(d)}
                    disabled={busyId === d.id}
                  >
                    {d.kind === 'link'
                      ? 'Open'
                      : busyId === d.id
                      ? 'Fetching…'
                      : 'Download'}
                  </button>
                  <button
                    type="button"
                    className={btnGhost}
                    onClick={() => {
                      if (window.confirm(`Remove "${d.name}"?`)) {
                        remove.mutate(d.id);
                      }
                    }}
                    disabled={remove.isPending}
                  >
                    Remove
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
};

export default AttachmentsPanel;
